import { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  TablePagination,
  Chip,
  Button,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useSavedQueries } from '../../../../hooks/QueryBuilder/useSavedQueries';
import { useQueryResult } from '../../../../hooks/QueryBuilder/useQueryResult';
import { useQueryCount } from '../../../../hooks/QueryBuilder/useQueryCount';
import { FilterHeader } from '../../../Clients/QueryBuilder/components/FilterHeader';
import { AvailableGroup } from '../../../../context/QueryBuilderDataContext';
import { QueryField } from '../../../../types/QueryBuilder';
import { isAdvancedQuerySource } from '../../../../utils/queryBuilderUtils';
import QueryPreviewDialog, { QueryPreviewTarget } from './QueryPreviewDialog';
import { toast } from 'react-toastify';

interface ExistingQueriesPickerProps {
  selectedQueryId: number | null;
  availableGroups: AvailableGroup[];
  loadingGroups: boolean;
  fields: QueryField[];
  categories: string[];
  onRefetchGroups: () => void;
  onSelect: (query: { queryId: number; description: string; nbRecipients: number | null }) => void;
}

function ExistingQueriesPicker({ selectedQueryId, availableGroups, loadingGroups, fields, categories, onRefetchGroups, onSelect }: ExistingQueriesPickerProps) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([]);
  const [isFromCompany, setIsFromCompany] = useState<0 | 1 | undefined>(undefined);
  const [source, setSource] = useState<'SIMPLE' | 'ADVANCED' | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState('');
  const [submittedSearchTerm, setSubmittedSearchTerm] = useState('');
  const [selectingId, setSelectingId] = useState<string | null>(null);
  const [previewQuery, setPreviewQuery] = useState<QueryPreviewTarget | null>(null);

  const groupQueryIds = selectedGroupIds.length > 0 ? selectedGroupIds.join(',') : undefined;

  const { queries, totalItems, loading } = useSavedQueries({
    page: page + 1,
    itemsPerPage: rowsPerPage,
    groupQueryIds,
    isFromCompany,
    search: submittedSearchTerm,
    source,
  });

  const { fetchQueryResult } = useQueryResult();
  const { launchQueryCount } = useQueryCount();

  const handleReset = () => {
    setSelectedGroupIds([]);
    setIsFromCompany(undefined);
    setSource(undefined);
    setSearchTerm('');
    setSubmittedSearchTerm('');
    setPage(0);
  };

  const handleSearchSubmit = (value: string) => {
    setPage(0);
    setSubmittedSearchTerm(value.trim());
  };

  const handleSelectQuery = async (queryId: string, description: string) => {
    setSelectingId(queryId);
    const toastId = toast.loading('Récupération du nombre de destinataires...');
    try {
      const res = await fetchQueryResult(queryId);
      const status = res?.status?.toUpperCase();
      const isExpired = !status || status.includes('EXPIRED') || status.includes('UNKNOWN') || status === 'ERROR';
      let count = res?.count ?? res?.listCount?.reduce((acc, i) => acc + i.count, 0) ?? null;

      if (isExpired || count === null) {
        const relaunch = await launchQueryCount(queryId);
        if (relaunch?.count !== undefined) {
          count = relaunch.count;
        } else {
          toast.update(toastId, {
            render: 'Requête sélectionnée — comptage en cours, revenez plus tard pour voir le nombre de destinataires.',
            type: 'info',
            isLoading: false,
            autoClose: 6000,
          });
          onSelect({ queryId: Number(queryId), description, nbRecipients: null });
          return;
        }
      }

      toast.update(toastId, {
        render: `Requête sélectionnée : ${count?.toLocaleString('fr-FR')} destinataire(s)`,
        type: 'success',
        isLoading: false,
        autoClose: 4000,
      });
      onSelect({ queryId: Number(queryId), description, nbRecipients: count });
    } catch {
      toast.update(toastId, { render: 'Erreur lors de la récupération du résultat', type: 'error', isLoading: false, autoClose: 5000 });
    } finally {
      setSelectingId(null);
    }
  };

  return (
    <Box>
      <FilterHeader
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onSearchSubmit={handleSearchSubmit}
        selectedGroupIds={selectedGroupIds}
        onSelectedGroupIdsChange={(ids) => { setSelectedGroupIds(ids); setPage(0); }}
        availableGroups={availableGroups}
        loadingGroups={loadingGroups}
        isFromCompany={isFromCompany}
        onTypeChange={(value) => { setIsFromCompany(value); setPage(0); }}
        source={source}
        onSourceChange={(value) => { setSource(value); setPage(0); }}
        onReset={handleReset}
        totalItems={totalItems}
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : queries.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="body2" color="text.secondary">Aucune requête trouvée</Typography>
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
            <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
              <colgroup>
                <col style={{ width: '35%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '20%' }} />
              </colgroup>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Groupe</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {queries.map((query) => {
                  const isSelected = selectedQueryId !== null && String(selectedQueryId) === query.QUERY_ID;
                  return (
                    <TableRow key={query.QUERY_ID} hover selected={isSelected}>
                      <TableCell sx={{ overflow: 'hidden' }}>
                        <Typography variant="body2" noWrap title={query.DESCRIPTION || query.QUERY_NAME || ''}>
                          {query.DESCRIPTION || query.QUERY_NAME || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {query.QUERY_GROUP_NAME && (
                          <Chip label={query.QUERY_GROUP_NAME} size="small" sx={{ bgcolor: '#e3f2fd', color: '#1976d2' }} />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(query.APPLICATION_DATE).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={isAdvancedQuerySource(query.SOURCE) ? 'Avancé' : 'Simple'}
                          size="small"
                          sx={isAdvancedQuerySource(query.SOURCE)
                            ? { bgcolor: '#fff3e0', color: '#e65100' }
                            : { bgcolor: '#e8f5e9', color: '#2e7d32' }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant={isSelected ? 'contained' : 'outlined'}
                          color={isSelected ? 'success' : 'primary'}
                          startIcon={isSelected ? <CheckCircleOutlineIcon fontSize="small" /> : undefined}
                          disabled={selectingId === query.QUERY_ID}
                          onClick={() => setPreviewQuery({
                            queryId: query.QUERY_ID,
                            description: query.DESCRIPTION || query.QUERY_NAME || '',
                          })}
                          sx={{ textTransform: 'none' }}
                        >
                          {selectingId === query.QUERY_ID ? <CircularProgress size={16} /> : isSelected ? 'Sélectionnée' : 'Sélectionner'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalItems}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            labelRowsPerPage="Lignes par page"
            sx={{ mt: 1 }}
          />
        </>
      )}

      <QueryPreviewDialog
        open={previewQuery !== null}
        query={previewQuery}
        fields={fields}
        categories={categories}
        confirming={previewQuery !== null && selectingId === previewQuery.queryId}
        onClose={() => setPreviewQuery(null)}
        onConfirm={() => {
          if (!previewQuery) return;
          handleSelectQuery(previewQuery.queryId, previewQuery.description);
          setPreviewQuery(null);
        }}
      />
    </Box>
  );
}

export default ExistingQueriesPicker;
